import type { RootState } from '@/store';

import { USER_LOAN_PROGRAM } from '@/config/constants';
import type { CalculateClosingDisclosureProps } from '.';
import { calculateEnhancedInterestRate } from './rateIntegration';

const calculateSecondMortgageInterestRate = (
  baseRate: number,
  creditScore: number,
  ltv: number,
  reduxState?: RootState
) => {
  // 2nd mortgage rates are typically higher than primary mortgages
  if (reduxState) {
    return calculateEnhancedInterestRate(
      reduxState,
      'SecondMortgage',
      baseRate,
      creditScore,
      ltv
    );
  }
  return baseRate;
};

export const calculateSecondMortgageClosingDisclosure = (
  props: CalculateClosingDisclosureProps & Record<string, any>,
  reduxState?: RootState
) => {
  const {
    purchasePrice,
    downPayment,
    creditScore,
    propertyType,
    occupancy,
    state: propertyState,
    county,
    city,
    zipCode,
    propertyTaxRate,
    homeownersInsuranceRate,
    hoaFees,
    currentLoanBalance,
    requestedLoanAmount,
    marketInterestRate = 9.0, // 2nd mortgage rates are typically higher
  } = props as any;

  // 2nd mortgage specific calculations
  const secondMortgageAmount = requestedLoanAmount || 75000; // Default $75k loan
  const loanAmount = purchasePrice - (downPayment || 0);
  const ltv = purchasePrice ? loanAmount / purchasePrice : 0.8;
  const secondMortgageInterestRate = calculateSecondMortgageInterestRate(
    marketInterestRate || 9.0,
    Number(creditScore) || 700,
    Number(ltv) || 0.8,
    reduxState
  );

  // 2nd mortgage terms
  const loanTerm = 300; // 25 year term is common for 2nd mortgages

  // 2nd mortgage specific fees
  const appraisalFeeBase = 700; // Standard appraisal fee
  const processingFeeBase = 700; // Standard processing fee
  const underwritingFee = 500; // Standard underwriting fee
  const applicationFeeBase = 300; // Standard application fee

  // 2nd mortgage specific fees
  const secondMortgageFee = secondMortgageAmount * 0.015; // 1.5% second mortgage fee
  const subordinationFee = 200; // Fee to subordinate to primary mortgage

  // Title and settlement fees
  const titleSettlementAgentFee = 1200; // Standard title fee
  const titleLendersTitleInsuranceBase = (secondMortgageAmount * 0.004).toFixed(
    2
  ); // Standard rate

  // Recording fees
  const countyRecordingFees = 50;
  const stateRecordingFees = 50;
  const recordingTitle = 50;
  const recordingMortgage = 50;
  const recordingDeedBase = 50;

  // Transfer taxes (typically not applicable for 2nd mortgage)
  const transferTaxes = 0;

  // Prepaid costs (minimal for 2nd mortgage)
  const prepaidsPropertyTaxes = 0; // No prepaid taxes for 2nd mortgage
  const prepaidsHomeownersInsurance = 0; // No prepaid insurance for 2nd mortgage
  const prepaidInterest =
    (secondMortgageAmount * secondMortgageInterestRate * 15) / (365 * 100); // 15 days

  // Monthly payment calculation
  const monthlyPrincipalInterest =
    (secondMortgageAmount *
      (secondMortgageInterestRate / 100) *
      Math.pow(1 + secondMortgageInterestRate / 100, loanTerm)) /
    (Math.pow(1 + secondMortgageInterestRate / 100, loanTerm) - 1);
  const monthlyPayment = monthlyPrincipalInterest;

  // Lender cost totals
  const lenderCostTotals =
    appraisalFeeBase +
    processingFeeBase +
    underwritingFee +
    applicationFeeBase +
    secondMortgageFee +
    subordinationFee +
    titleSettlementAgentFee +
    parseFloat(titleLendersTitleInsuranceBase) +
    countyRecordingFees +
    stateRecordingFees +
    recordingTitle +
    recordingMortgage +
    recordingDeedBase +
    transferTaxes;

  // Prepaid totals
  const prepaidTotals =
    prepaidsPropertyTaxes + prepaidsHomeownersInsurance + prepaidInterest;

  // Total closing costs
  const totalClosingCosts = lenderCostTotals + prepaidTotals;

  // Cash to close (minimal for 2nd mortgage)
  const cashToClose = totalClosingCosts;

  return {
    type: USER_LOAN_PROGRAM.SECOND_MORTGAGE,
    loanAmount: secondMortgageAmount,
    interestRate: secondMortgageInterestRate,
    monthlyPayment,
    monthlyPrincipalInterest,
    loanTerm,
    appraisalFeeBase,
    processingFeeBase,
    underwritingFee,
    applicationFeeBase,
    secondMortgageFee,
    subordinationFee,
    titleSettlementAgentFee,
    titleLendersTitleInsuranceBase,
    countyRecordingFees,
    stateRecordingFees,
    recordingTitle,
    recordingMortgage,
    recordingDeedBase,
    transferTaxes,
    prepaidsPropertyTaxes,
    prepaidsHomeownersInsurance,
    prepaidInterest,
    lenderCostTotals,
    prepaidTotals,
    totalClosingCosts,
    cashToClose,
    // 2nd mortgage specific properties
    minimumOwnersTitle: 1650,
    maximumOwnersTitle: 5850,
    optionalOwnersTitleInsurance: Math.min(
      Math.max(purchasePrice * 0.00575, 1650),
      5850
    ),
    minimumInspectionFee: 650,
    maximumInspectionFee: 4500,
    homeInspectionFee: Math.min(Math.max(500, 650), 4500),
    // Refinance specific properties
    currentLoanBalance,
    requestedLoanAmount: secondMortgageAmount,
  };
};
